import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { AuthorArticles, AuthorArticlesSkeleton } from '../../components/AuthorArticles';
import { AuthorArticleListPaginationContainer as PaginationContainer } from '../Pagination';
import { PostAuthorArticlesLayout } from '../../components/PostAuthorArticlesLayout';
import { selectAuthorArticles } from '../../selectors/authorArticles';
import { selectListingAppendType } from '../../selectors/settings/selectListingAppendType';
import { selectPost } from '../../selectors/post';
import {
  ApplicationContext,
  parsePostCardPath,
  preparePageNumber,
  useGetAuthorArticles,
  useTimeoutEffect,
} from '../../utils';
import { getAuthorArticles, resetAuthorArticles } from '../../actions/authorArticles';
import { TThunkDispatch } from '../../types/redux';
import { ERequestStatus } from '../../types/requestStatus';
import { EOrdering } from '../../repositories/journal/v1/get-articles-author';

/** Список статей автора */
export const AuthorArticlesNewContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { items: articles, status } = useSelector(selectAuthorArticles);
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const qsPage = preparePageNumber(searchParams.get('page') as string);
  const post = useSelector(selectPost);
  const setType = useSelector(selectListingAppendType);
  const {
    custom: { authorArticlesLimit },
  } = useContext(ApplicationContext);
  const [prevPathname, setPrevPathname] = useState(pathname);
  const { id: qsPostId } = parsePostCardPath(pathname);
  const [isSkeletonShown, setIsSkeletonShown] = useState(status === ERequestStatus.Loading && setType === 'set');

  /**
   * Сбрасывает статьи автора при переходе с поста на пост
   */
  useEffect(() => {
    if (pathname !== prevPathname) {
      dispatch(resetAuthorArticles());
    }
  }, [dispatch, pathname, prevPathname]);

  useGetAuthorArticles(
    useCallback(() => {
      if (
        !('id' in post) ||
        post.id !== qsPostId ||
        !authorArticlesLimit ||
        !post.attributes.rubrics?.includes(32) ||
        !post.attributes.seoTitle
      ) {
        return;
      }

      void dispatch(
        getAuthorArticles({
          authorName: post.attributes.seoTitle,
          offset: (qsPage - 1) * authorArticlesLimit,
          limit: authorArticlesLimit,
          ordering: EOrdering.Num_views,
          setType,
        }),
      );

      setPrevPathname(pathname);
    }, [authorArticlesLimit, dispatch, pathname, post, qsPage, qsPostId, setType]),
    { dependencyNames: ['pathname', 'page'], scrollRef: wrapperRef },
  );

  /** Создает небольшую задержку перед скрытием скелетона, когда элементов не найдено */
  useTimeoutEffect(() => {
    setIsSkeletonShown(status === ERequestStatus.Loading && setType === 'set');
  }, [!isSkeletonShown || articles.length ? 0 : 500]);

  /**
   * Сбрасывает статьи автора при размонтировании
   */
  useEffect(() => {
    return () => {
      dispatch(resetAuthorArticles());
    };
  }, [dispatch]);

  if (isSkeletonShown) {
    return <AuthorArticlesSkeleton />;
  }

  if (!articles?.length) {
    return null;
  }

  return (
    <PostAuthorArticlesLayout
      ref={wrapperRef}
      articles={<AuthorArticles items={articles} />}
      pagination={<PaginationContainer />}
    />
  );
};
